import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell,
  MessageSquare,
  Calendar,
  Users,
  Clock,
  Check,
  CheckCheck,
  Trash2,
  Settings,
} from "lucide-react";

// Notification type icons
const getNotificationIcon = (type) => {
  switch (type) {
    case "chat":
      return <MessageSquare className="w-4 h-4 text-blue-500" />;
    case "shared_edit":
      return <Calendar className="w-4 h-4 text-green-500" />;
    case "team_invite":
      return <Users className="w-4 h-4 text-purple-500" />;
    case "reminder":
      return <Clock className="w-4 h-4 text-orange-500" />;
    default:
      return <Bell className="w-4 h-4 text-gray-500" />;
  }
};

// Format time ago
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Single notification item
const NotificationItem = ({ notification, onMarkRead, onDelete, onClick }) => {
  return (
    <div
      className={`p-3 border-b border-stone-100 last:border-0 cursor-pointer transition-colors ${
        notification.is_read ? "bg-white" : "bg-green-50/50"
      } hover:bg-stone-50 group`}
      onClick={() => onClick(notification)}
      data-testid={`notification-item-${notification.notification_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getNotificationIcon(notification.type)}</div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${notification.is_read ? "text-stone-600" : "text-[#1E3A2F] font-medium"}`}>
            {notification.title}
          </p>
          <p className="text-xs text-stone-500 truncate">{notification.message}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-stone-400">
              {formatTimeAgo(notification.created_at)}
            </span>
            {notification.team_name && (
              <span className="text-xs text-stone-400">
                · {notification.team_name}
              </span>
            )}
          </div>
        </div>
        {/* Always visible action buttons */}
        <div className="flex items-center gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
          {!notification.is_read && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(notification.notification_id);
              }}
              title="Mark as read"
            >
              <Check className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(notification.notification_id);
            }}
            title="Remove notification"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    fetchNotifications,
  } = useNotifications();
  const [open, setOpen] = useState(false);

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }

    // Navigate based on type
    if (notification.type === "chat" && notification.team_id) {
      navigate(`/dashboard/teams?team=${notification.team_id}`);
    } else if (notification.type === "team_invite") {
      navigate("/dashboard/teams");
    } else if (notification.type === "shared_edit") {
      if (notification.resource_type === "calendar_event") {
        navigate("/dashboard/calendar");
      } else if (notification.resource_type === "todo_item") {
        navigate("/dashboard/todos");
      } else {
        navigate("/dashboard");
      }
    } else if (notification.type === "reminder") {
      navigate("/dashboard/reminders");
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2"
          data-testid="notification-bell"
          onClick={() => {
            setOpen(true);
            fetchNotifications();
          }}
        >
          <Bell className="w-5 h-5 text-stone-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-stone-200">
          <h3 className="font-semibold text-[#1E3A2F]">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={markAllAsRead}
                title="Mark all as read"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                All Read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={deleteAllNotifications}
                title="Clear all notifications"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => {
                setOpen(false);
                navigate("/settings/notifications");
              }}
              title="Notification settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Notifications list */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Bell className="w-12 h-12 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.notification_id}
                notification={notification}
                onMarkRead={markAsRead}
                onDelete={deleteNotification}
                onClick={handleNotificationClick}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-stone-200">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-sm text-[#2D5A3D]"
              onClick={() => {
                setOpen(false);
                navigate("/settings/notifications");
              }}
            >
              View All & Manage Settings
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
